import React, { useState, useEffect } from 'react';
import { X, Layers, AlertTriangle } from 'lucide-react';
import Button from '../ui/Button';
import { ClassAllocation } from '../../types/grid';
import { displayLearningArea } from '../../constants/learningAreas';

interface CreateCompositeClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedClasses: ClassAllocation[];
  onCreateComposite: (compositeName: string, subClassIds: string[]) => Promise<void>;
}

const CreateCompositeClassModal: React.FC<CreateCompositeClassModalProps> = ({
  isOpen,
  onClose,
  selectedClasses,
  onCreateComposite
}) => {
  const [compositeName, setCompositeName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Generate default composite name when modal opens
  useEffect(() => {
    if (isOpen && selectedClasses.length > 0) {
      // Create name like "Math 10 A/B" or "English 11 A/B/C"
      const classNames = selectedClasses.map(c => {
        // Extract the last part (usually the class letter/number)
        const parts = c.className.split(' ');
        return parts[parts.length - 1];
      }).join('/');

      // Use the first class as base and append the combined letters
      const baseClass = selectedClasses[0].className;
      const baseParts = baseClass.split(' ');
      baseParts[baseParts.length - 1] = classNames;

      setCompositeName(baseParts.join(' '));
    }
  }, [isOpen, selectedClasses]);

  const handleCreate = async () => {
    if (!compositeName.trim()) return;

    setIsCreating(true);
    try {
      await onCreateComposite(
        compositeName.trim(),
        selectedClasses.map(c => c.id)
      );
      onClose();
    } catch (error) {
      console.error('Failed to create composite class:', error);
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  // Check for warnings
  const differentSubjects = new Set(selectedClasses.map(c => c.subjectCode || c.className.split(' ')[0])).size > 1;
  const differentYearLevels = new Set(selectedClasses.map(c => c.yearLevel).filter(Boolean)).size > 1;
  const differentCurriculumAreas = new Set(selectedClasses.map(c => c.curriculumArea)).size > 1;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-start">
          <div className="flex items-center space-x-3">
            <div className="bg-navy-100 p-2 rounded-lg">
              <Layers className="h-6 w-6 text-navy-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-ink-900">
                Create Composite Class
              </h3>
              <p className="text-sm text-ink-500 mt-1">
                Combine {selectedClasses.length} classes into one teaching allocation
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-ink-400 hover:text-ink-500"
            disabled={isCreating}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Composite Class Name */}
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Composite Class Name
            </label>
            <input
              type="text"
              value={compositeName}
              onChange={(e) => setCompositeName(e.target.value)}
              className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
              placeholder="e.g., Math 10 A/B"
              autoFocus
            />
            <p className="text-xs text-ink-500 mt-1">
              This will be the name displayed in the timetable
            </p>
          </div>

          {/* Warnings */}
          {(differentSubjects || differentYearLevels || differentCurriculumAreas) && (
            <div className="bg-warn-bg border border-warn-border rounded-md p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-warn-fg mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <h4 className="font-medium text-warn-fg mb-1">Potential Issues Detected</h4>
                  <ul className="list-disc list-inside text-warn-fg space-y-1">
                    {differentSubjects && (
                      <li>Selected classes have different subjects</li>
                    )}
                    {differentYearLevels && (
                      <li>Selected classes have different year levels</li>
                    )}
                    {differentCurriculumAreas && (
                      <li>Selected classes have different learning areas</li>
                    )}
                  </ul>
                  <p className="mt-2">You can still create this composite, but make sure this is intentional.</p>
                </div>
              </div>
            </div>
          )}

          {/* Selected Classes List */}
          <div>
            <h4 className="text-sm font-medium text-ink-700 mb-3">
              Classes Being Combined ({selectedClasses.length})
            </h4>
            <div className="border border-ink-200 rounded-md divide-y divide-ink-200">
              {selectedClasses.map((classItem, index) => (
                <div key={classItem.id} className="p-3 hover:bg-ink-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-ink-900">
                        {index + 1}. {classItem.className}
                      </div>
                      <div className="text-sm text-ink-500 mt-1">
                        {displayLearningArea(classItem.curriculumArea)}
                        {classItem.yearLevel && ` • Year ${classItem.yearLevel}`}
                        {classItem.subjectCode && ` • ${classItem.subjectCode}`}
                      </div>
                    </div>
                    {/* Show current line assignments */}
                    <div className="font-mono tabular-nums text-xs text-ink-400">
                      {Object.keys(classItem.lineAssignments).length} line(s)
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-navy-50 border border-navy-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-navy-800 mb-2">What happens when you create a composite?</h4>
            <ul className="text-sm text-navy-700 space-y-1 list-disc list-inside">
              <li>A new composite class will be created with the name you specified</li>
              <li>The original classes will be preserved but hidden from the main view</li>
              <li>Line assignments from the first selected class will be copied to the composite</li>
              <li>Only one teacher allocation is needed (saves {selectedClasses.length - 1}× teaching minutes)</li>
              <li>You can "break apart" the composite later to restore the original classes</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-between">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!compositeName.trim() || isCreating}
            loading={isCreating}
          >
            Create Composite Class
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateCompositeClassModal;
